import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';

import { MainLayout } from '../../layouts/MainLayout';
import { CreateVote } from '../Votes/CreateVote';
import { useMatchQuery } from '../../generated/graphql';
import { Votes } from '../Votes/Votes';
import { Summary } from '../Votes/Summary';
import { Loader } from '../../components/LoadingContainer';
import { NotFound } from '../NotFound';
import { paths } from '../../paths';
import { ReadVotes } from '../Votes/ReadVotes';
import { MatchHome } from './MatchHome';
import { Availability } from './availability/Availability';
import { MatchSettingsRouter } from './MatchSettingsRouter';
import { ShareMatchEmail } from './ShareMatchEmail';
import { MatchReport } from './PostMatch/MatchReport';
import { MatchHeader } from './MatchHeader';

interface Props extends RouteComponentProps<{ id: string }> {}

export const MatchRouter: React.FC<Props> = ({ match: { params } }) => {
  const [getMatchResponse, executeMatchQuery] = useMatchQuery({
    variables: { id: params.id },
    requestPolicy: 'cache-and-network',
  });

  return pipe(
    O.fromNullable(getMatchResponse.data?.match),
    O.fold(
      () => (getMatchResponse.fetching ? <Loader /> : <NotFound />),
      (match) => {
        return (
          <MainLayout
            key="match"
            header={
              <MatchHeader
                match={match}
                reloadMatch={() =>
                  executeMatchQuery({ requestPolicy: 'cache-and-network' })
                }
              />
            }
          >
            <Switch>
              {match.team.isCaptain ? (
                <Route
                  render={() => <MatchSettingsRouter match={match} />}
                  path={paths.match.settings.home()}
                />
              ) : null}
              <Route
                render={() => <MatchHome match={match} />}
                exact={true}
                path={paths.match.home()}
              />
              <Route
                render={() => <MatchReport match={match} />}
                exact={true}
                path={paths.match.matchReport()}
              />

              <Route
                render={() => <Availability match={match} />}
                exact={true}
                path={paths.match.availability()}
              />
              <Route
                render={() => <Votes match={match} />}
                exact={true}
                path={paths.match.votes.vote()}
              />

              <Route
                render={() => <ReadVotes match={match} />}
                exact={true}
                path={paths.match.votes.read()}
              />
              <Route
                render={() => <Summary match={match} />}
                exact={true}
                path={paths.match.votes.summary()}
              />

              <Route
                render={({
                  match: {
                    params: { voteTypeId },
                  },
                }: RouteComponentProps<{ voteTypeId: string }>) => (
                  <CreateVote match={match} voteTypeId={voteTypeId} />
                )}
                exact={true}
                path={paths.match.votes.create()}
              />
              <Route
                render={() => <ShareMatchEmail match={match} />}
                exact={true}
                path="/m/:id/share/email"
              />
              <Route
                render={() => <Redirect to={paths.match.home(match.id)} />}
              />
            </Switch>
          </MainLayout>
        );
      },
    ),
  );
};
